import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { Button } from '../../../../ui/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../../../ui/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../../ui/components/ui/form';
import { Input } from '../../../../ui/components/ui/input';
import { Textarea } from '../../../../ui/components/ui/textarea';
import { CreateProductAddOnRequestDto } from '../../types/request';
import { ProductAddOnDetailsResponseDto } from '../../types/response';

const addOnFormSchema = z.object({
  title: z.string().trim().min(1, 'Titel ist erforderlich').max(120, 'Maximal 120 Zeichen'),
  description: z.string().max(2000, 'Maximal 2000 Zeichen')
});

type AddOnFormValues = z.infer<typeof addOnFormSchema>;

const buildDefaults = (addOn?: ProductAddOnDetailsResponseDto | null): AddOnFormValues => ({
  title: addOn?.title ?? '',
  description: addOn?.description ?? ''
});

export interface AddOnFormDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Edit mode when set. */
  addOn?: ProductAddOnDetailsResponseDto | null;
  /** Resolves `true` on success; the dialog only closes then. */
  onSubmit: (payload: CreateProductAddOnRequestDto) => Promise<boolean>;
}

const AddOnFormDialog = ({ isOpen, onOpenChange, addOn, onSubmit }: AddOnFormDialogProps) => {
  const isEdit = !!addOn;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<AddOnFormValues>({
    resolver: zodResolver(addOnFormSchema),
    defaultValues: buildDefaults(addOn)
  });

  useEffect(() => {
    if (!isOpen) return;
    form.reset(buildDefaults(addOn));
  }, [isOpen, addOn, form]);

  const handleSubmit = async (values: AddOnFormValues) => {
    setIsSubmitting(true);
    try {
      const isSuccess = await onSubmit({ title: values.title.trim(), description: values.description.trim() });
      if (isSuccess) onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md min-w-[520px] py-4 px-6 gap-5 bg-white" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="text-[25px] font-bold text-[#12282A]">{isEdit ? 'Add-on bearbeiten' : 'Neues Add-on'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-5">
            <p className="text-[12px] text-[#888]">Add-ons sind immer inklusive und haben keinen eigenen Preis.</p>

            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-[10px]">
                  <FormLabel className="text-[14px] font-bold text-[#12282A]">Titel *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="z. B. Persönliche Ansprechperson" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-[10px]">
                  <FormLabel className="text-[14px] font-bold text-[#12282A]">Beschreibung</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Einfacher Text, Zeilenumbrüche bleiben erhalten" className="min-h-32" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Speichert…' : isEdit ? 'Speichern' : 'Erstellen'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddOnFormDialog;
